/*
  drawlz-size.js — the sitewide persistent size, one source of truth.

  Loaded from layout/theme.liquid so EVERY page participates: the header
  chip, the homepage size band, collection grids, product rails and the
  PDP all read and write the same state and re-render off the same event.

  State lives in localStorage['drawlz_size'].

  window.DrawlzSize
    .get()            -> 'L' | null
    .set(size)        -> stores it; passing the size that is ALREADY set
                         CLEARS it (click the active pill to deselect),
                         then dispatches 'drawlz:size' either way
    .clear()          -> explicit clear
    .subscribe(fn)    -> fn(size) now and on every change; returns an
                         unsubscribe function
    .isUnderwearScale(sizes) -> should this product reshape?
    .reshape(root, o) -> sort/mark dzc cards inside root for the current size

  Event: document 'drawlz:size', detail {size}. Fired once on load too, so
  a listener registered before or after boot always initialises correctly.
*/
window.DrawlzSize = window.DrawlzSize || (function () {
  var KEY = 'drawlz_size';
  var booted = false;

  /* the underwear scale — the only scale the reshape applies to. Socks
     (S/M, L/XL) and any other scale fall out of it automatically. */
  var SCALE = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

  function read() {
    try { return localStorage.getItem(KEY) || null; } catch (e) { return null; }
  }
  function write(size) {
    try {
      if (size) localStorage.setItem(KEY, size);
      else localStorage.removeItem(KEY);
    } catch (e) {}
  }
  function emit(size) {
    document.dispatchEvent(new CustomEvent('drawlz:size', { detail: { size: size } }));
  }

  function get() { return read(); }

  function set(size) {
    /* re-clicking the active size deselects it — a shopper must always be
       able to get back to "show me everything" */
    var next = (size && size === read()) ? null : (size || null);
    write(next);
    emit(next);
    return next;
  }

  function clear() { return set(null) === null ? null : null; }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    var handler = function (e) { fn(e.detail ? e.detail.size : null); };
    document.addEventListener('drawlz:size', handler);
    fn(read());                                   /* initialise immediately */
    return function () { document.removeEventListener('drawlz:size', handler); };
  }

  /* A product reshapes only if every one of its sizes is on the underwear
     scale. NOTE: a brief stocked only S-3XL and the SuedeSkin crew (S-3XL)
     are indistinguishable by values alone — mark non-underwear products
     with data-size-scale="other" on the card to opt them out explicitly. */
  function isUnderwearScale(sizes) {
    if (!sizes || !sizes.length) return false;
    for (var i = 0; i < sizes.length; i++) {
      if (SCALE.indexOf(String(sizes[i]).trim()) === -1) return false;
    }
    return true;
  }

  function cardSizes(card) {
    var raw = card.getAttribute('data-sizes') || '';
    return raw ? raw.split(',').filter(Boolean) : [];
  }

  /* The out-of-size line is a BUTTON, not text, so the whole
     "Sold out in {size} · Notify me" is one tap target that opens the same
     back-in-stock sheet the PDP uses (drawlz-bis.js, already sitewide).

     It carries only data-handle + data-size. Resolving the variant id here
     would mean printing a variant map onto every card in every grid; instead
     drawlz-bis.js fetches /products/{handle}.js once, on open, for the one
     product the shopper actually asked about.

     The icon is stroke-drawn with currentColor so it inherits the same red
     the line already uses — no second colour to keep in sync. */
  var MAIL_SVG =
    '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<rect x="2" y="4" width="20" height="16" rx="2"></rect>' +
    '<path d="m2 7 10 6 10-6"></path></svg>';

  function setOosHint(hint, card, size) {
    var handle = card.getAttribute('data-handle') || '';
    var label = 'Sold out in ' + size + ' · Notify me';

    /* no handle means we cannot resolve a product — leave it as plain text
       rather than shipping a button that opens an empty sheet. */
    if (!handle) { hint.textContent = label; return; }

    hint.textContent = '';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dzc-hint__bis';
    btn.setAttribute('data-drawlz-bis-card', '');
    btn.setAttribute('data-size', size);
    btn.setAttribute('data-handle', handle);
    btn.setAttribute('aria-label', 'Notify me when ' + size + ' is back in stock');
    btn.innerHTML = MAIL_SVG;
    var span = document.createElement('span');
    span.textContent = label;
    btn.appendChild(span);
    hint.appendChild(btn);
  }

  /* Sort ready-in-size first and label the rest — never hide, never
     dead-end. Cards keep their DOM identity so quick view still works. */
  function reshape(root, opts) {
    if (!root) return;
    opts = opts || {};
    var size = read();
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-dzc-card]'));
    if (!cards.length) return;

    var ready = [], notInSize = [], excluded = [];
    cards.forEach(function (card) {
      var axis = card.getAttribute('data-size-axis');
      var sizes = cardSizes(card);
      var optedOut = card.getAttribute('data-size-scale') === 'other';
      var participates = !!size && axis !== 'none' && !optedOut && isUnderwearScale(sizes);

      card.classList.remove('is-ready-size', 'is-oos-size');
      var hint = card.querySelector('[data-dzc-hint]');

      if (!participates) {
        if (hint) hint.textContent = '';
        excluded.push(card);
        return;
      }
      if (sizes.indexOf(size) !== -1) {
        card.classList.add('is-ready-size');
        if (hint) hint.textContent = '✓ Ready in ' + size;
        ready.push(card);
      } else {
        card.classList.add('is-oos-size');
        if (hint) setOosHint(hint, card, size);
        notInSize.push(card);
      }
    });

    if (!size || opts.sort === false) return;
    /* ready first, then everything that doesn't reshape, then out-of-size */
    var order = ready.concat(excluded, notInSize);
    order.forEach(function (card) { card.parentNode.appendChild(card); });
  }

  /* boot: publish the stored size once the DOM can hear it */
  function boot() {
    if (booted) return;
    booted = true;
    emit(read());
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  return {
    get: get,
    set: set,
    clear: clear,
    subscribe: subscribe,
    isUnderwearScale: isUnderwearScale,
    reshape: reshape,
    SCALE: SCALE
  };
})();